/**
 * Purchasing message catalogue. Namespaced under `purchasing.*` and merged
 * into the app's `common` namespace by CLAUDE-A; every key exists in BOTH
 * `en` and `ar`. Components call `t(key, "English default")`, so this file is
 * the single place the Arabic copy lives.
 *
 * Wire enums (`pending_approval`, `partially_received`, `ordered_elsewhere`…)
 * are never rendered raw — each one has a label here.
 */

const en = {
  purchasing: {
    nav: {
      purchaseOrders: "Purchase orders",
      goodsReceipts: "Goods receipts",
      landedCosts: "Landed costs",
      reorderSuggestions: "Reorder suggestions",
      supplierItems: "Supplier items",
      settings: "Purchasing settings",
    },
    permissions: {
      code: "403 · ACCESS CONTROL",
      title: "Purchasing access restricted",
    },
    common: {
      all: "All",
      yes: "Yes",
      no: "No",
      loading: "Loading…",
      clearFilters: "Clear filters",
      anySupplier: "Any supplier",
      pickSupplier: "Choose a supplier…",
      searchSupplier: "Search supplier…",
      noSuppliers: "No suppliers found.",
      pickVariant: "Choose a SKU…",
      searchSku: "Search SKU…",
      noVariants: "No variants found.",
    },
    poStatus: {
      draft: "Draft",
      pending_approval: "Pending approval",
      approved: "Approved",
      sent: "Sent",
      partially_received: "Partially received",
      received: "Received",
      closed: "Closed",
    },
    grnStatus: {
      posted: "Posted",
    },
    landedCostStatus: {
      draft: "Draft",
      posted: "Posted",
    },
    landedCostType: {
      freight: "Freight",
      duty: "Duty",
      insurance: "Insurance",
      other: "Other",
    },
    landedCostBasis: {
      value: "Value",
      weight: "Weight",
      quantity: "Quantity",
    },
    reorderStatus: {
      open: "Open",
      converted: "Converted",
      dismissed: "Dismissed",
      superseded: "Superseded",
    },
    reorderDismissReason: {
      ordered_elsewhere: "Ordered elsewhere",
      temporary_exception: "Temporary exception",
      not_required: "Not required",
      data_issue: "Data issue",
    },
    transmissionStatus: {
      queued: "Queued",
      sent: "Sent",
      failed: "Failed",
    },
    transmissionFailure: {
      delivery_rejected: "Delivery rejected",
      provider_unavailable: "Provider unavailable",
      provider_authentication_failed: "Provider authentication failed",
      rate_limited: "Rate limited",
      timeout: "Timed out",
      unknown: "Unknown failure",
    },
    poList: {
      title: "Purchase orders",
      subtitle: "Draft, approve, send and receive supplier orders.",
      newPo: "New purchase order",
      empty: "No purchase orders yet.",
      emptyFiltered: "No purchase orders match. Clear a filter and try again.",
      loadError: "Purchase orders could not be loaded.",
      columns: {
        number: "PO #",
        supplier: "Supplier",
        status: "Status",
        total: "Total",
        orderDate: "Ordered",
        expected: "Expected",
        created: "Created",
      },
      filters: {
        q: "PO number or reference",
        qPlaceholder: "PO-2026-000001",
        status: "Status",
        supplier: "Supplier",
        expectedFrom: "Expected from",
        expectedTo: "Expected to",
      },
    },
    poEditor: {
      newTitle: "New purchase order",
      loadError: "This purchase order could not be loaded.",
      headerTitle: "Order header",
      supplier: "Supplier",
      currency: "Currency",
      orderDate: "Order date",
      expectedDate: "Expected delivery",
      supplierReference: "Supplier reference",
      shippingTotal: "Shipping total",
      totalLine: "Total",
      createDraft: "Create draft",
      saveChanges: "Save changes",
      deleteDraft: "Delete draft",
      submit: "Submit for approval",
      submitted: "Submitted for approval.",
      autoApproved: "Auto-approved — below the approval threshold.",
      send: "Send to supplier",
      resend: "Resend to supplier",
      sendTitle: "Send to supplier",
      sendBody:
        "The rendered purchase order is emailed to the supplier. Leave the recipient blank to use the supplier's default purchasing contact.",
      recipient: "Recipient email",
      confirmSend: "Send",
      receive: "Receive goods",
      close: "Close purchase order",
      closeTitle: "Close purchase order",
      closeBody:
        "Closing is available once the order is fully received. A closed order accepts no further receipts.",
      readOnly: "This purchase order is read-only in its current status.",
      reapprovalWarning:
        "Edits that raise the total beyond the configured tolerance return this order to pending approval and notify approvers.",
      reapprovalTitle: "Returned to pending approval",
      reapprovalBody:
        "The saved total moved beyond the configured tolerance, so the approval was cleared and approvers were notified.",
      approvalRequiredHint: "Approval required on submit",
      autoApproveHint: "Auto-approves on submit",
      threshold: "Threshold",
      approverRole: "Approver role",
      rule: "Rule",
      addLine: "Add line",
      linesRequired: "Add at least one line.",
      subtotalPreview: "Subtotal (preview)",
      grandTotalPreview: "Grand total (preview)",
      viewReceipts: "View goods receipts for this order",
      transmissionsEmpty: "Not yet sent.",
      attempt: "Attempt {{n}}",
      openDocument: "Open document",
      tabs: {
        lines: "Lines",
        transmissions: "Transmissions",
      },
      line: {
        variant: "Variant",
        variantOf: "Variant on line {{n}}",
        pickVariant: "Choose a SKU…",
        supplierSku: "Supplier SKU",
        supplierSkuOf: "Supplier SKU on line {{n}}",
        uom: "UoM",
        uomOf: "UoM on line {{n}}",
        qty: "Qty",
        qtyOf: "Quantity on line {{n}}",
        unitCost: "Unit cost",
        unitCostOf: "Unit cost on line {{n}}",
        tax: "Tax",
        taxOf: "Tax on line {{n}}",
        lineTotal: "Line total",
        remove: "Remove line {{n}}",
        moq: "MOQ",
        pack: "Pack",
        lead: "Lead {{days}}d",
        lastCost: "Last cost",
        belowMoq: "Below the supplier minimum order quantity.",
        currencyMismatch:
          "Supplier item is priced in {{currency}} — no automatic conversion is applied.",
      },
    },
    receive: {
      title: "Receive goods",
      loadError: "This purchase order could not be loaded.",
      notReceivable: "This purchase order is not open for receiving in its current status.",
      backToOrder: "Back to the purchase order",
      scanLabel: "Scan barcode",
      barcodeUnknown: "No variant for barcode {{barcode}}",
      variantNotOnPo: "{{sku}} is not on this purchase order.",
      scanFailed: "Barcode lookup failed. Try again.",
      warehouse: "Warehouse",
      pickWarehouse: "Choose…",
      warehouseRequired: "Choose a receiving warehouse.",
      occurredAt: "Received at",
      deliveryReference: "Delivery note reference",
      ordered: "Ordered",
      received: "Received",
      remaining: "Remaining",
      lineComplete: "Line complete",
      thisReceipt: "This receipt",
      rejected: "Rejected",
      location: "Location",
      pickLocation: "Choose…",
      locationRequired: "Choose a location for this line.",
      batch: "Batch",
      pickBatch: "Choose…",
      increase: "Increase {{sku}}",
      decrease: "Decrease {{sku}}",
      overReceiptWarning:
        "Above the allowed remaining quantity of {{allowed}} (tolerance {{pct}}%). The server may refuse this line.",
      acceptedRequired: "Accepted quantity must be above zero — reduce the rejected quantity.",
      nothingEntered: "Enter a received quantity on at least one line.",
      nothingLeft: "Nothing left to receive on this order.",
      enteredLines: "{{count}} line(s) entered",
      post: "Post goods receipt",
      posted: "Goods receipt {{number}} posted.",
    },
    grnList: {
      title: "Goods receipts",
      subtitle: "Every posted receipt against a purchase order.",
      empty: "No goods receipts yet.",
      emptyFiltered: "No goods receipts match these filters.",
      loadError: "Goods receipts could not be loaded.",
      openPo: "Open order",
      columns: {
        number: "GRN #",
        purchaseOrder: "Purchase order",
        warehouse: "Warehouse",
        receivedAt: "Received",
        deliveryReference: "Delivery note",
        created: "Created",
      },
      filters: {
        supplier: "Supplier",
        warehouse: "Warehouse",
        from: "Received from",
        to: "Received to",
      },
    },
    grnDetail: {
      loadError: "This goods receipt could not be loaded.",
      openPo: "Open purchase order",
      backToList: "Back to goods receipts",
      linesTitle: "Received lines",
      totalValue: "Total received value",
      eventsTitle: "Business events",
      noEvents: "No goods-received events on this receipt.",
      columns: {
        variant: "Variant",
        batch: "Batch",
        received: "Received",
        accepted: "Accepted",
        rejected: "Rejected",
        unitCost: "Unit cost",
        value: "Inventory value",
        movement: "Stock movement",
      },
    },
    landedCostList: {
      title: "Landed costs",
      subtitle: "Freight, duty and insurance allocated onto received stock.",
      newDocument: "New landed cost",
      empty: "No landed costs yet.",
      emptyFiltered: "No landed costs match these filters.",
      loadError: "Landed costs could not be loaded.",
      columns: {
        number: "Document",
        supplier: "Supplier",
        status: "Status",
        total: "Total",
        baseTotal: "Base total",
        postedAt: "Posted",
        created: "Created",
      },
      filters: {
        status: "Status",
        supplier: "Supplier",
        createdFrom: "Created from",
      },
    },
    landedCost: {
      newTitle: "New landed cost",
      loadError: "This landed cost could not be loaded.",
      postedReadOnly: "A posted landed cost is immutable.",
      step1: "1 · Costs",
      step2: "2 · Select receipt lines",
      step3: "3 · Preview and post",
      costsTitle: "① Cost components",
      selectTitle: "② Select receipt lines",
      previewTitle: "③ Preview and post",
      supplier: "Supplier",
      currency: "Currency",
      baseCurrency: "Base currency",
      fxRate: "FX rate",
      invoiceReference: "Supplier invoice reference",
      addCost: "Add cost",
      total: "Total",
      baseTotal: "Base total",
      createDraft: "Create draft",
      save: "Save draft",
      delete: "Delete draft",
      occurredAt: "Posting date",
      preview: "Recalculate preview",
      previewColumns: {
        line: "Receipt line",
        basis: "Basis",
        share: "Share %",
        allocated: "Allocated",
        priorCost: "Prior unit cost",
        revisedCost: "Revised unit cost",
        cogs: "COGS variance",
        allocatedTotal: "Allocated total",
        revaluation: "Inventory revaluation",
        rounding: "Rounding remainder",
      },
      previewEmpty: "Select receipt lines and recalculate to see the server's allocation.",
      post: "Post landed cost",
      posted: "Landed cost posted.",
      reconciles: "Σ allocations equals Σ costs — ready to post.",
      doesNotReconcile: "Σ allocations does not equal Σ costs — posting is blocked.",
      selectedCount: "{{count}} receipt line(s) selected",
      noEligibleGrns:
        "No posted goods receipts are eligible. Only posted receipts in the document's base currency can be allocated.",
      grnLoadError: "Receipt lines could not be loaded.",
      grnPermission: "Reading goods receipts requires purchasing.grn.read.",
      line: {
        type: "Type",
        typeOf: "Cost type {{n}}",
        chargeCode: "Charge code",
        chargeCodeOf: "Charge code {{n}}",
        basis: "Basis",
        basisOf: "Basis {{n}}",
        amount: "Amount",
        amountOf: "Amount {{n}}",
        baseAmount: "Base amount",
        baseAmountOf: "Base amount {{n}}",
        remove: "Remove cost {{n}}",
      },
    },
    reorder: {
      title: "Reorder suggestions",
      noRun: "No calculation run has produced suggestions yet.",
      lastRun: "Last calculation {{when}}",
      empty: "No suggestions in this view.",
      loadError: "Suggestions could not be loaded.",
      convertSelected: "Convert {{count}} to draft POs",
      dismissSelected: "Dismiss {{count}} selected",
      converted: "{{count}} draft purchase order(s) created",
      openConverted: "Open converted PO",
      groupCount: "{{count}} suggestion(s)",
      selectAll: "Select all",
      selectGroup: "Select all for {{name}}",
      selectRow: "Select {{sku}}",
      onHand: "on hand",
      onOrder: "on order",
      reserved: "reserved",
      backorder: "backorder",
      rop: "ROP",
      suggested: "Suggested",
      moqLead: "MOQ {{moq}} · lead {{days}}d",
      dismissTitle: "Dismiss suggestions",
      dismissBody: "Dismissed rows stay auditable and are hidden until the next calculation run.",
      reason: "Reason",
      filters: {
        status: "Status",
        supplier: "Supplier",
        warehouse: "Warehouse",
        expected: "Expected delivery on new POs",
      },
    },
    supplierItems: {
      title: "Supplier items",
      subtitle:
        "Purchasing defaults per supplier and variant. Last cost is maintained by receipt history.",
      new: "New supplier item",
      editTitle: "Edit supplier item",
      editBody: "Last cost is not editable — it is derived from receipt history.",
      edit: "Edit {{sku}}",
      delete: "Delete {{sku}}",
      empty:
        "No supplier items yet — they appear as purchase orders are received, and can also be added manually.",
      emptyFiltered: "No supplier items match these filters.",
      loadError: "Supplier items could not be loaded.",
      duplicate: "This supplier already has a record for that variant.",
      supplier: "Supplier",
      supplierRequired: "Choose a supplier.",
      variant: "Variant",
      variantRequired: "Choose a variant.",
      supplierSku: "Supplier SKU",
      currency: "Currency",
      moq: "Minimum order quantity",
      pack: "Pack size",
      lead: "Lead time (days)",
      leadInvalid: "Enter whole days, zero or more.",
      reorderPoint: "Reorder point",
      safetyStock: "Safety stock",
      targetStock: "Target stock",
      tolerance: "Over-receipt tolerance %",
      isPreferred: "Preferred supplier for this variant",
      isActive: "Active",
      preferredYes: "Preferred",
      columns: {
        supplierSku: "Supplier SKU",
        variant: "Variant",
        supplier: "Supplier",
        lastCost: "Last cost",
        moq: "MOQ",
        pack: "Pack",
        lead: "Lead days",
        preferred: "Preferred",
        updated: "Updated",
      },
      filters: {
        supplier: "Supplier",
        q: "Supplier SKU",
        qPlaceholder: "Prefix search…",
        preferred: "Preferred only",
      },
    },
    config: {
      title: "Purchasing configuration",
      subtitle:
        "Effective tolerances applied by purchase-order approval and goods-receipt validation. Read-only.",
      loadError: "Purchasing configuration could not be loaded.",
      companyTitle: "Company defaults",
      overReceipt: "Over-receipt tolerance",
      reapproval: "Re-approval tolerance",
      scope: "Tolerance scope",
      scopeValue: {
        global: "Global",
        category: "Per category",
      },
      effectiveAt: "Effective from",
      overridesTitle: "Category overrides",
      noOverrides: "No category overrides are configured.",
      category: "Category",
    },
  },
} as const;

const ar = {
  purchasing: {
    nav: {
      purchaseOrders: "أوامر الشراء",
      goodsReceipts: "إشعارات استلام البضائع",
      landedCosts: "التكاليف المحمّلة",
      reorderSuggestions: "اقتراحات إعادة الطلب",
      supplierItems: "أصناف الموردين",
      settings: "إعدادات المشتريات",
    },
    permissions: {
      code: "٤٠٣ · التحكم في الوصول",
      title: "الوصول إلى المشتريات مقيّد",
    },
    common: {
      all: "الكل",
      yes: "نعم",
      no: "لا",
      loading: "جارٍ التحميل…",
      clearFilters: "مسح عوامل التصفية",
      anySupplier: "أي مورّد",
      pickSupplier: "اختر مورّدًا…",
      searchSupplier: "ابحث عن مورّد…",
      noSuppliers: "لا يوجد موردون.",
      pickVariant: "اختر رمز صنف…",
      searchSku: "ابحث برمز الصنف…",
      noVariants: "لا توجد أصناف.",
    },
    poStatus: {
      draft: "مسودة",
      pending_approval: "بانتظار الاعتماد",
      approved: "معتمد",
      sent: "مُرسل",
      partially_received: "مستلم جزئيًا",
      received: "مستلم",
      closed: "مغلق",
    },
    grnStatus: {
      posted: "مُرحّل",
    },
    landedCostStatus: {
      draft: "مسودة",
      posted: "مُرحّل",
    },
    landedCostType: {
      freight: "شحن",
      duty: "رسوم جمركية",
      insurance: "تأمين",
      other: "أخرى",
    },
    landedCostBasis: {
      value: "القيمة",
      weight: "الوزن",
      quantity: "الكمية",
    },
    reorderStatus: {
      open: "مفتوح",
      converted: "مُحوّل",
      dismissed: "مُستبعد",
      superseded: "مُستبدل",
    },
    reorderDismissReason: {
      ordered_elsewhere: "تم الطلب من جهة أخرى",
      temporary_exception: "استثناء مؤقت",
      not_required: "غير مطلوب",
      data_issue: "خلل في البيانات",
    },
    transmissionStatus: {
      queued: "في قائمة الانتظار",
      sent: "أُرسل",
      failed: "فشل",
    },
    transmissionFailure: {
      delivery_rejected: "رُفض التسليم",
      provider_unavailable: "مزوّد الخدمة غير متاح",
      provider_authentication_failed: "فشل التحقق لدى مزوّد الخدمة",
      rate_limited: "تم تجاوز حد المعدل",
      timeout: "انتهت المهلة",
      unknown: "فشل غير معروف",
    },
    poList: {
      title: "أوامر الشراء",
      subtitle: "إنشاء أوامر الموردين واعتمادها وإرسالها واستلامها.",
      newPo: "أمر شراء جديد",
      empty: "لا توجد أوامر شراء بعد.",
      emptyFiltered: "لا توجد أوامر شراء مطابقة. امسح أحد عوامل التصفية وحاول مجددًا.",
      loadError: "تعذّر تحميل أوامر الشراء.",
      columns: {
        number: "رقم الأمر",
        supplier: "المورّد",
        status: "الحالة",
        total: "الإجمالي",
        orderDate: "تاريخ الأمر",
        expected: "التسليم المتوقع",
        created: "تاريخ الإنشاء",
      },
      filters: {
        q: "رقم الأمر أو المرجع",
        qPlaceholder: "PO-2026-000001",
        status: "الحالة",
        supplier: "المورّد",
        expectedFrom: "متوقع من",
        expectedTo: "متوقع إلى",
      },
    },
    poEditor: {
      newTitle: "أمر شراء جديد",
      loadError: "تعذّر تحميل أمر الشراء هذا.",
      headerTitle: "بيانات الأمر",
      supplier: "المورّد",
      currency: "العملة",
      orderDate: "تاريخ الأمر",
      expectedDate: "التسليم المتوقع",
      supplierReference: "مرجع المورّد",
      shippingTotal: "إجمالي الشحن",
      totalLine: "الإجمالي",
      createDraft: "إنشاء مسودة",
      saveChanges: "حفظ التغييرات",
      deleteDraft: "حذف المسودة",
      submit: "إرسال للاعتماد",
      submitted: "تم الإرسال للاعتماد.",
      autoApproved: "اعتُمد تلقائيًا — أقل من حد الاعتماد.",
      send: "إرسال إلى المورّد",
      resend: "إعادة الإرسال إلى المورّد",
      sendTitle: "إرسال إلى المورّد",
      sendBody:
        "يُرسل أمر الشراء المُنسّق إلى المورّد بالبريد الإلكتروني. اترك المستلم فارغًا لاستخدام جهة اتصال المشتريات الافتراضية للمورّد.",
      recipient: "البريد الإلكتروني للمستلم",
      confirmSend: "إرسال",
      receive: "استلام البضائع",
      close: "إغلاق أمر الشراء",
      closeTitle: "إغلاق أمر الشراء",
      closeBody: "يتاح الإغلاق بعد الاستلام الكامل. الأمر المغلق لا يقبل أي استلام إضافي.",
      readOnly: "أمر الشراء للقراءة فقط في حالته الحالية.",
      reapprovalWarning:
        "التعديلات التي ترفع الإجمالي فوق حد التجاوز المعتمد تُعيد هذا الأمر إلى انتظار الاعتماد وتُشعر المعتمدين.",
      reapprovalTitle: "أُعيد إلى انتظار الاعتماد",
      reapprovalBody:
        "تجاوز الإجمالي المحفوظ حد التجاوز المعتمد، لذلك أُلغي الاعتماد السابق وأُشعر المعتمدون.",
      approvalRequiredHint: "يتطلب اعتمادًا عند الإرسال",
      autoApproveHint: "يُعتمد تلقائيًا عند الإرسال",
      threshold: "الحد",
      approverRole: "دور المعتمد",
      rule: "القاعدة",
      addLine: "إضافة بند",
      linesRequired: "أضف بندًا واحدًا على الأقل.",
      subtotalPreview: "الإجمالي الفرعي (معاينة)",
      grandTotalPreview: "الإجمالي الكلي (معاينة)",
      viewReceipts: "عرض إشعارات الاستلام لهذا الأمر",
      transmissionsEmpty: "لم يُرسل بعد.",
      attempt: "المحاولة {{n}}",
      openDocument: "فتح المستند",
      tabs: {
        lines: "البنود",
        transmissions: "عمليات الإرسال",
      },
      line: {
        variant: "الصنف",
        variantOf: "الصنف في البند {{n}}",
        pickVariant: "اختر رمز صنف…",
        supplierSku: "رمز صنف المورّد",
        supplierSkuOf: "رمز صنف المورّد في البند {{n}}",
        uom: "وحدة القياس",
        uomOf: "وحدة القياس في البند {{n}}",
        qty: "الكمية",
        qtyOf: "الكمية في البند {{n}}",
        unitCost: "تكلفة الوحدة",
        unitCostOf: "تكلفة الوحدة في البند {{n}}",
        tax: "الضريبة",
        taxOf: "الضريبة في البند {{n}}",
        lineTotal: "إجمالي البند",
        remove: "حذف البند {{n}}",
        moq: "أقل كمية",
        pack: "حجم العبوة",
        lead: "مدة التوريد {{days}} يوم",
        lastCost: "آخر تكلفة",
        belowMoq: "أقل من الحد الأدنى لكمية الطلب لدى المورّد.",
        currencyMismatch: "صنف المورّد مُسعّر بعملة {{currency}} — لا يُطبَّق تحويل تلقائي.",
      },
    },
    receive: {
      title: "استلام البضائع",
      loadError: "تعذّر تحميل أمر الشراء هذا.",
      notReceivable: "أمر الشراء غير مفتوح للاستلام في حالته الحالية.",
      backToOrder: "العودة إلى أمر الشراء",
      scanLabel: "مسح الباركود",
      barcodeUnknown: "لا يوجد صنف للباركود {{barcode}}",
      variantNotOnPo: "الصنف {{sku}} غير موجود في أمر الشراء هذا.",
      scanFailed: "فشل البحث عن الباركود. حاول مرة أخرى.",
      warehouse: "المستودع",
      pickWarehouse: "اختر…",
      warehouseRequired: "اختر مستودع الاستلام.",
      occurredAt: "وقت الاستلام",
      deliveryReference: "مرجع إشعار التسليم",
      ordered: "المطلوب",
      received: "المستلم",
      remaining: "المتبقي",
      lineComplete: "اكتمل البند",
      thisReceipt: "هذا الاستلام",
      rejected: "المرفوض",
      location: "الموقع",
      pickLocation: "اختر…",
      locationRequired: "اختر موقعًا لهذا البند.",
      batch: "الدفعة",
      pickBatch: "اختر…",
      increase: "زيادة {{sku}}",
      decrease: "إنقاص {{sku}}",
      overReceiptWarning:
        "أعلى من الكمية المتبقية المسموح بها {{allowed}} (حد التجاوز {{pct}}٪). قد يرفض الخادم هذا البند.",
      acceptedRequired: "يجب أن تكون الكمية المقبولة أكبر من صفر — قلّل الكمية المرفوضة.",
      nothingEntered: "أدخل كمية مستلمة في بند واحد على الأقل.",
      nothingLeft: "لم يتبقّ شيء للاستلام في هذا الأمر.",
      enteredLines: "تم إدخال {{count}} بند",
      post: "ترحيل إشعار الاستلام",
      posted: "تم ترحيل إشعار الاستلام {{number}}.",
    },
    grnList: {
      title: "إشعارات استلام البضائع",
      subtitle: "كل استلام مُرحّل مقابل أمر شراء.",
      empty: "لا توجد إشعارات استلام بعد.",
      emptyFiltered: "لا توجد إشعارات استلام مطابقة لعوامل التصفية.",
      loadError: "تعذّر تحميل إشعارات الاستلام.",
      openPo: "فتح الأمر",
      columns: {
        number: "رقم الإشعار",
        purchaseOrder: "أمر الشراء",
        warehouse: "المستودع",
        receivedAt: "تاريخ الاستلام",
        deliveryReference: "إشعار التسليم",
        created: "تاريخ الإنشاء",
      },
      filters: {
        supplier: "المورّد",
        warehouse: "المستودع",
        from: "الاستلام من",
        to: "الاستلام إلى",
      },
    },
    grnDetail: {
      loadError: "تعذّر تحميل إشعار الاستلام هذا.",
      openPo: "فتح أمر الشراء",
      backToList: "العودة إلى إشعارات الاستلام",
      linesTitle: "البنود المستلمة",
      totalValue: "إجمالي القيمة المستلمة",
      eventsTitle: "أحداث الأعمال",
      noEvents: "لا توجد أحداث استلام في هذا الإشعار.",
      columns: {
        variant: "الصنف",
        batch: "الدفعة",
        received: "المستلم",
        accepted: "المقبول",
        rejected: "المرفوض",
        unitCost: "تكلفة الوحدة",
        value: "قيمة المخزون",
        movement: "حركة المخزون",
      },
    },
    landedCostList: {
      title: "التكاليف المحمّلة",
      subtitle: "الشحن والرسوم والتأمين مُوزّعة على المخزون المستلم.",
      newDocument: "تكلفة محمّلة جديدة",
      empty: "لا توجد تكاليف محمّلة بعد.",
      emptyFiltered: "لا توجد تكاليف محمّلة مطابقة لعوامل التصفية.",
      loadError: "تعذّر تحميل التكاليف المحمّلة.",
      columns: {
        number: "المستند",
        supplier: "المورّد",
        status: "الحالة",
        total: "الإجمالي",
        baseTotal: "الإجمالي بالعملة الأساسية",
        postedAt: "تاريخ الترحيل",
        created: "تاريخ الإنشاء",
      },
      filters: {
        status: "الحالة",
        supplier: "المورّد",
        createdFrom: "أُنشئ من",
      },
    },
    landedCost: {
      newTitle: "تكلفة محمّلة جديدة",
      loadError: "تعذّر تحميل هذه التكلفة المحمّلة.",
      postedReadOnly: "التكلفة المحمّلة المُرحّلة غير قابلة للتعديل.",
      step1: "١ · التكاليف",
      step2: "٢ · اختيار بنود الاستلام",
      step3: "٣ · المعاينة والترحيل",
      costsTitle: "① مكوّنات التكلفة",
      selectTitle: "② اختيار بنود الاستلام",
      previewTitle: "③ المعاينة والترحيل",
      supplier: "المورّد",
      currency: "العملة",
      baseCurrency: "العملة الأساسية",
      fxRate: "سعر الصرف",
      invoiceReference: "مرجع فاتورة المورّد",
      addCost: "إضافة تكلفة",
      total: "الإجمالي",
      baseTotal: "الإجمالي بالعملة الأساسية",
      createDraft: "إنشاء مسودة",
      save: "حفظ المسودة",
      delete: "حذف المسودة",
      occurredAt: "تاريخ الترحيل",
      preview: "إعادة حساب المعاينة",
      previewColumns: {
        line: "بند الاستلام",
        basis: "أساس التوزيع",
        share: "النسبة ٪",
        allocated: "المبلغ المُوزّع",
        priorCost: "تكلفة الوحدة السابقة",
        revisedCost: "تكلفة الوحدة المعدّلة",
        cogs: "فرق تكلفة المبيعات",
        allocatedTotal: "إجمالي المُوزّع",
        revaluation: "إعادة تقييم المخزون",
        rounding: "فرق التقريب",
      },
      previewEmpty: "اختر بنود الاستلام ثم أعد الحساب لعرض توزيع الخادم.",
      post: "ترحيل التكلفة المحمّلة",
      posted: "تم ترحيل التكلفة المحمّلة.",
      reconciles: "مجموع التوزيعات يساوي مجموع التكاليف — جاهز للترحيل.",
      doesNotReconcile: "مجموع التوزيعات لا يساوي مجموع التكاليف — الترحيل متوقف.",
      selectedCount: "تم اختيار {{count}} بند استلام",
      noEligibleGrns:
        "لا توجد إشعارات استلام مُرحّلة مؤهلة. تُوزَّع فقط الاستلامات المُرحّلة بنفس العملة الأساسية للمستند.",
      grnLoadError: "تعذّر تحميل بنود الاستلام.",
      grnPermission: "قراءة إشعارات الاستلام تتطلب الصلاحية purchasing.grn.read.",
      line: {
        type: "النوع",
        typeOf: "نوع التكلفة {{n}}",
        chargeCode: "رمز الرسم",
        chargeCodeOf: "رمز الرسم {{n}}",
        basis: "أساس التوزيع",
        basisOf: "أساس التوزيع {{n}}",
        amount: "المبلغ",
        amountOf: "المبلغ {{n}}",
        baseAmount: "المبلغ بالعملة الأساسية",
        baseAmountOf: "المبلغ بالعملة الأساسية {{n}}",
        remove: "حذف التكلفة {{n}}",
      },
    },
    reorder: {
      title: "اقتراحات إعادة الطلب",
      noRun: "لم تُنتج أي دورة حساب اقتراحات بعد.",
      lastRun: "آخر دورة حساب {{when}}",
      empty: "لا توجد اقتراحات في هذا العرض.",
      loadError: "تعذّر تحميل الاقتراحات.",
      convertSelected: "تحويل {{count}} إلى مسودات أوامر شراء",
      dismissSelected: "استبعاد {{count}} المحددة",
      converted: "تم إنشاء {{count}} مسودة أمر شراء",
      openConverted: "فتح أمر الشراء المُحوّل",
      groupCount: "{{count}} اقتراح",
      selectAll: "تحديد الكل",
      selectGroup: "تحديد الكل لـ {{name}}",
      selectRow: "تحديد {{sku}}",
      onHand: "المتاح",
      onOrder: "قيد الطلب",
      reserved: "محجوز",
      backorder: "طلبات مؤجلة",
      rop: "نقطة إعادة الطلب",
      suggested: "المقترح",
      moqLead: "أقل كمية {{moq}} · مدة التوريد {{days}} يوم",
      dismissTitle: "استبعاد الاقتراحات",
      dismissBody: "تبقى الصفوف المستبعدة قابلة للتدقيق وتُخفى حتى دورة الحساب التالية.",
      reason: "السبب",
      filters: {
        status: "الحالة",
        supplier: "المورّد",
        warehouse: "المستودع",
        expected: "التسليم المتوقع في الأوامر الجديدة",
      },
    },
    supplierItems: {
      title: "أصناف الموردين",
      subtitle: "إعدادات الشراء لكل مورّد وصنف. آخر تكلفة تُحدَّث من سجل الاستلامات.",
      new: "صنف مورّد جديد",
      editTitle: "تعديل صنف المورّد",
      editBody: "آخر تكلفة غير قابلة للتعديل — تُشتق من سجل الاستلامات.",
      edit: "تعديل {{sku}}",
      delete: "حذف {{sku}}",
      empty:
        "لا توجد أصناف موردين بعد — تظهر تلقائيًا مع استلام أوامر الشراء، ويمكن إضافتها يدويًا.",
      emptyFiltered: "لا توجد أصناف موردين مطابقة لعوامل التصفية.",
      loadError: "تعذّر تحميل أصناف الموردين.",
      duplicate: "لدى هذا المورّد سجل لهذا الصنف بالفعل.",
      supplier: "المورّد",
      supplierRequired: "اختر مورّدًا.",
      variant: "الصنف",
      variantRequired: "اختر صنفًا.",
      supplierSku: "رمز صنف المورّد",
      currency: "العملة",
      moq: "الحد الأدنى لكمية الطلب",
      pack: "حجم العبوة",
      lead: "مدة التوريد (أيام)",
      leadInvalid: "أدخل عدد أيام صحيحًا، صفرًا أو أكثر.",
      reorderPoint: "نقطة إعادة الطلب",
      safetyStock: "مخزون الأمان",
      targetStock: "المخزون المستهدف",
      tolerance: "نسبة السماح بتجاوز الاستلام ٪",
      isPreferred: "المورّد المفضّل لهذا الصنف",
      isActive: "نشط",
      preferredYes: "مفضّل",
      columns: {
        supplierSku: "رمز صنف المورّد",
        variant: "الصنف",
        supplier: "المورّد",
        lastCost: "آخر تكلفة",
        moq: "أقل كمية",
        pack: "العبوة",
        lead: "أيام التوريد",
        preferred: "مفضّل",
        updated: "آخر تحديث",
      },
      filters: {
        supplier: "المورّد",
        q: "رمز صنف المورّد",
        qPlaceholder: "بحث ببداية الرمز…",
        preferred: "المفضّل فقط",
      },
    },
    config: {
      title: "إعدادات المشتريات",
      subtitle: "حدود التجاوز المطبّقة على اعتماد أوامر الشراء والتحقق من الاستلام. للقراءة فقط.",
      loadError: "تعذّر تحميل إعدادات المشتريات.",
      companyTitle: "الإعدادات الافتراضية للشركة",
      overReceipt: "حد السماح بتجاوز الاستلام",
      reapproval: "حد إعادة الاعتماد",
      scope: "نطاق حد التجاوز",
      scopeValue: {
        global: "عام",
        category: "حسب الفئة",
      },
      effectiveAt: "ساري من",
      overridesTitle: "استثناءات الفئات",
      noOverrides: "لا توجد استثناءات فئات مُعدّة.",
      category: "الفئة",
    },
  },
} as const;

export const purchasingMessages = { en, ar };
